import { memo, useCallback, useEffect, useMemo, useState } from "react";
import type { InspectionState } from "@/views/inspections/components/inspections-lists/InspectionsLists.d";
import searchGray from "@/assets/images/inspections-table/searchGray.svg";
import SearcherSelect from "./SearcherSelect";
import { useTranslate } from "@/hooks/use-translate/useTranslate";
import SecondaryButton from "@/components/buttons/secondary-button/SecondaryButton";
import { useDate } from "@/hooks/use-date/useDate";
import { Icon } from "@/components/icons/Icon";
import { getFieldsToSearch } from "../../utils/inspections.utils";

interface TableSearchersProps {
  tableType: InspectionState;
  rendered: boolean;
  lastFiltered: string;
  closeSearchers: () => void;
  searchInspections: (params: string) => void;
}

const TableSearchers = ({
  tableType = "active",
  rendered = false,
  closeSearchers,
  searchInspections,
  lastFiltered,
}: TableSearchersProps) => {
  const translate = useTranslate();
  const { transforDateToSearch } = useDate();

  const [snStand, setSnStand] = useState("");
  const [esnEnter, setEsnEnter] = useState("");
  const [esnExit, setEsnExit] = useState("");
  const [engineId, setEngineId] = useState("");
  const [colour, setColour] = useState("");
  const [phase, setPhase] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startDateError, setStartDateError] = useState("");
  const [endDateError, setEndDateError] = useState("");
  const [startDateToSearch, setStartDateToSearch] = useState("");
  const [endDateToSearch, setEndDateToSearch] = useState("");

  const fieldsToSearch = useMemo(
    () => getFieldsToSearch(tableType),
    [tableType],
  );

  useEffect(() => {
    if (!rendered || lastFiltered.length === 0) {
      setSnStand("");
      setEsnEnter("");
      setEsnExit("");
      setEngineId("");
      setColour("");
      setPhase("");
      setStartDate("");
      setEndDate("");
      setStartDateError("");
      setEndDateError("");
      setStartDateToSearch("");
      setEndDateToSearch("");
    }
  }, [rendered, lastFiltered.length]);

  useEffect(() => {
    if (startDate.length === 10 && !startDateError)
      setStartDateToSearch(transforDateToSearch(startDate));
    else setStartDateToSearch("");
  }, [startDate, endDate, transforDateToSearch, startDateError]);

  useEffect(() => {
    if (endDate.length === 10 && !endDateError)
      setEndDateToSearch(transforDateToSearch(endDate));
    else setEndDateToSearch("");
  }, [endDate, endDateError, transforDateToSearch]);

  const changeText = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      switch (event.target.id) {
        case "snStand":
          setSnStand(event.target.value);
          break;
        case "esnEnter":
          setEsnEnter(event.target.value);
          break;
        case "esnExit":
          setEsnExit(event.target.value);
          break;
        default:
          break;
      }
    },
    [],
  );

  const changeSelectorValue = useCallback((value: string, title: string) => {
    switch (title) {
      case "engineType":
        setEngineId(value);
        break;
      case "colour":
        setColour(value);
        break;
      case "phase":
        setPhase(value);
        break;
      default:
        break;
    }
  }, []);

  const changeDate = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const dateId = event.target.id;
      const _isValidDate = (dateStr: string): boolean => {
        const regex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
        if (!regex.test(dateStr)) return false;

        const [day, month, year] = dateStr.split("/").map(Number);
        const date = new Date(year, month - 1, day);

        if (
          date.getFullYear() !== year ||
          date.getMonth() !== month - 1 ||
          date.getDate() !== day
        ) {
          return false;
        }

        const today = new Date();
        return date <= today;
      };

      const _formatDateInput = (value: string): string => {
        const digits = value.replace(/\D/g, "");

        let formatted = "";
        if (digits.length <= 2) {
          formatted = digits;
        } else if (digits.length <= 4) {
          formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
        } else {
          formatted = `${digits.slice(0, 2)}/${digits.slice(
            2,
            4,
          )}/${digits.slice(4, 8)}`;
        }

        return formatted;
      };
      const newValue = event.target.value;
      const formattedValue = _formatDateInput(newValue);

      const invalidDate = translate("invalidOrFutureDate");

      if (dateId === "startDate") {
        setStartDate(formattedValue);
        if (formattedValue.length === 10 && !_isValidDate(formattedValue)) {
          setStartDateError(invalidDate);
        } else {
          setStartDateError("");
        }
      } else {
        setEndDate(formattedValue);
        if (formattedValue.length === 10 && !_isValidDate(formattedValue)) {
          setEndDateError(invalidDate);
        } else {
          setEndDateError("");
        }
      }
    },
    [translate],
  );

  const createSearcher = useCallback(
    (type: string, name: string, options: string[] | null) => {
      let value = "";
      switch (name) {
        case "snStand":
          value = snStand;
          break;
        case "esnEnter":
          value = esnEnter;
          break;
        case "esnExit":
          value = esnExit;
          break;
        case "engineType":
          value = engineId;
          break;
        case "colour":
          value = colour;
          break;
        case "phase":
          value = phase;
          break;
        case "startDate":
          value = startDate;
          break;
        case "endDate":
          value = endDate;
          break;
        default:
          break;
      }

      switch (type) {
        case "string":
          return (
            <div className="relative w-full h-12">
              <input
                type="text"
                id={name}
                name={name}
                onChange={changeText}
                className="text-table-searcher"
                value={value}
              />
              {!value ? (
                <img
                  className="absolute right-1/2 transform translate-x-1/2 -translate-y-[32px] pointer-events-none z-0"
                  src={searchGray}
                />
              ) : null}
            </div>
          );
        case "select":
          return (
            <div className="relative w-full h-12">
              <SearcherSelect
                title={name}
                value={value}
                options={options as string[] | { id: number; name: string }[]}
                onChange={changeSelectorValue}
              />
              {!value ? (
                <img
                  className="absolute right-1/2 transform translate-x-1/2 -translate-y-[32px] pointer-events-none z-0"
                  src={searchGray}
                />
              ) : null}
            </div>
          );
        case "date":
          return (
            <div className="relative w-full h-12">
              <input
                type="text"
                id={name}
                value={value}
                onChange={changeDate}
                maxLength={10}
                className="text-table-searcher"
              />
              {startDateError && name === "startDate" ? (
                <div className="font-normal px-2 text-ib-red-main text-[12px] w-full absolute transform -translate-y-[18px] pointer-events-none z-0">
                  {startDateError}
                </div>
              ) : null}
              {endDateError && name === "endDate" ? (
                <div className="font-normal px-2 text-ib-red-main text-[12px] w-full absolute transform -translate-y-[18px] pointer-events-none z-0">
                  {endDateError}
                </div>
              ) : null}
              {!value ? (
                <img
                  className="absolute right-1/2 transform translate-x-1/2 -translate-y-[32px] pointer-events-none z-0"
                  src={searchGray}
                />
              ) : null}
            </div>
          );
        default:
          return name;
      }
    },
    [
      snStand,
      esnEnter,
      esnExit,
      engineId,
      colour,
      phase,
      startDate,
      endDate,
      endDateError,
      startDateError,
      changeDate,
      changeSelectorValue,
      changeText,
    ],
  );

  const disabledButton = useMemo((): boolean => {
    const noFiltersSelected =
      !snStand &&
      !esnEnter &&
      !esnExit &&
      !engineId &&
      !colour &&
      !phase &&
      !startDateToSearch &&
      !endDateToSearch;
    const dateErrors = endDateError.length > 0 || startDateError.length > 0;
    const invalidStartDate = !!(startDate && !startDateToSearch);
    const invalidEndDate = !!(endDate && !endDateToSearch);
    return (
      noFiltersSelected || dateErrors || invalidStartDate || invalidEndDate
    );
  }, [
    snStand,
    esnEnter,
    esnExit,
    engineId,
    colour,
    phase,
    startDateToSearch,
    endDateToSearch,
    endDateError,
    startDateError,
    startDate,
    endDate,
  ]);

  const search = useCallback(() => {
    const searchParams = [
      `?status=${tableType === "active" ? "IN_PROGRESS" : "CLOSED"}&size=20`,
    ];
    if (startDateToSearch) searchParams.push(`createdAt=${startDateToSearch}`);
    if (endDateToSearch) searchParams.push(`endDate=${endDateToSearch}`);
    if (snStand) searchParams.push(`nsStand=${snStand}`);
    if (esnEnter) searchParams.push(`esnIn=${esnEnter}`);
    if (esnExit) searchParams.push(`esnOut=${esnExit}`);
    if (phase) searchParams.push(`phaseId=${phase}`);
    if (engineId) searchParams.push(`engineId=${engineId}`);
    if (colour) searchParams.push(`colour=${colour}`);
    searchParams.push("page=");
    const params = searchParams.join("&").replaceAll(" ", "%20");
    searchInspections(params);
  }, [
    startDateToSearch,
    endDateToSearch,
    snStand,
    esnEnter,
    esnExit,
    phase,
    engineId,
    colour,
    tableType,
    searchInspections,
  ]);

  return (
    <>
      {rendered ? (
        <tr>
          <th className="bg-ib-gray-lightest h-12">
            <button type="button" onClick={closeSearchers}>
              <Icon
                name="closeEmptyCircle"
                className="size-5 text-ib-red-main"
              />
            </button>
          </th>
          {fieldsToSearch.map((field, index) => (
            <th key={`th-searcher-${index}`} className="searcher-cell">
              <>
                {createSearcher(field.type, field.name, field.data as string[])}
              </>
            </th>
          ))}
          <th className="bg-ib-gray-lightest h-14 text-center">
            <div className="w-fit" data-testid="search-button">
              <SecondaryButton
                label={translate("search")}
                colour="red"
                onClick={search}
                disabled={disabledButton}
              />
            </div>
          </th>
        </tr>
      ) : null}
    </>
  );
};

export default memo(TableSearchers);
